import { Breakpoint, SwipeableDrawer, SwipeableDrawerProps, SxProps, Theme, useMediaQuery } from "@mui/material";
import Drawer from "@mui/material/Drawer";
import React, { FC, ReactNode } from "react";
import BNBox from "../BNBox/BNBox";
import { BNModalHeader } from "./BNModalHeader";

export interface BNDrawerProps {
  title?: ReactNode;
  children: ReactNode;
  open: boolean;
  fullWidthContent?: boolean;
  onClose: (event?: React.KeyboardEvent | React.MouseEvent, reason?: string) => void;
  preventCloseOnDismiss?: boolean;
  classes?: any;
  maxWidth?: string | number;
  leftAnchoredWidth?: string | number;
  leftAnchoredMaxWidth?: string | number;
  className?: SwipeableDrawerProps["className"];
  important?: boolean;
  showCloseButton?: boolean;
  closeButtonText?: ReactNode;
  fullHeight?: boolean;
  minHeight?: string;
  id?: string;
  outerWrapperSx?: SxProps<Theme>;
  innerWrapperSx?: SxProps<Theme>;
  breakpoint?: Breakpoint;
}

const BNDrawer: FC<BNDrawerProps> = ({
  title,
  children,
  open,
  onClose,
  fullWidthContent,
  maxWidth,
  leftAnchoredWidth,
  leftAnchoredMaxWidth,
  preventCloseOnDismiss = false,
  classes,
  className,
  important,
  showCloseButton = true,
  closeButtonText,
  fullHeight,
  minHeight,
  id,
  outerWrapperSx,
  innerWrapperSx,
  breakpoint = "md",
}) => {
  const isDesktop = useMediaQuery<Theme>((theme) => theme.breakpoints.up(breakpoint));
  const anchor: SwipeableDrawerProps["anchor"] = isDesktop ? "left" : "bottom";

  const toggleDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent, reason: string) => {
    if (preventCloseOnDismiss && (reason == "escapeKeyDown" || reason == "backdropClick")) {
      return;
    }

    if (
      event &&
      event.type === "keydown" &&
      ((event as React.KeyboardEvent).key === "Tab" || (event as React.KeyboardEvent).key === "Shift")
    ) {
      return;
    }
    if (!open) {
      onClose(event, reason);
    }
  };
  const DrawerComponent = preventCloseOnDismiss ? Drawer : SwipeableDrawer;

  return (
    <DrawerComponent
      id={id}
      anchor={anchor}
      open={open}
      onClose={toggleDrawer(false) as any}
      onOpen={toggleDrawer(true) as any}
      disableSwipeToOpen
      sx={{
        // BottomNav is 1200
        // Kindley chat is 5005
        zIndex: important ? 5050 : 1300,
        flexShrink: 0,
        "& .MuiDrawer-paper":
          anchor == "bottom"
            ? {
                maxHeight: "90vh",
                minHeight: minHeight ?? (fullHeight ? "90vh" : "50vh"),
                borderTopLeftRadius: "3.2rem",
                borderTopRightRadius: "3.2rem",
                p: 2,
              }
            : anchor == "left"
              ? {
                  borderTopRightRadius: "3.2rem",
                  borderBottomRightRadius: "3.2rem",
                  p: 2,
                  width: leftAnchoredWidth ? leftAnchoredWidth : "auto",
                  maxWidth: leftAnchoredMaxWidth ? leftAnchoredMaxWidth : "auto",
                }
              : {},
      }}
      classes={classes}
      className={className}
      slotProps={{ paper: { elevation: 0 } }}
      data-uit-container={open ? "dialog" : "dialog-hidden"}
    >
      <BNBox
        sx={
          [
            {
              display: "flex",
              justifyContent: "center",
            },
            ...(outerWrapperSx ? [outerWrapperSx] : []),
          ] as SxProps<Theme>
        }
      >
        <BNBox
          sx={
            [
              {
                maxWidth: (maxWidth ?? fullWidthContent) ? "100%" : "48.5rem",
                width: fullWidthContent ? "100%" : "auto",
                // Avoid having Drawer content behind Noba Assistant Fab
                mb: 12,
              },
              ...(innerWrapperSx ? [innerWrapperSx] : []),
            ] as SxProps<Theme>
          }
        >
          <BNModalHeader
            onClose={() => onClose()}
            title={title}
            showCloseButton={!preventCloseOnDismiss && showCloseButton}
            closeButtonText={closeButtonText}
          />
          {children}
        </BNBox>
      </BNBox>
    </DrawerComponent>
  );
};
export default BNDrawer;
